import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Loader2, ShieldCheck, PoundSterling, Mail, ExternalLink, Copy, Send, AlertTriangle, Award } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { key: 'applied', label: 'Applied', color: 'bg-gray-100 text-gray-700' },
  { key: 'evidence', label: 'Evidence', color: 'bg-amber-100 text-amber-700' },
  { key: 'assessment', label: 'Assessment', color: 'bg-blue-100 text-blue-700' },
  { key: 'decision', label: 'Decision', color: 'bg-violet-100 text-violet-700' },
  { key: 'approved', label: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
  { key: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700' },
];
const NEXT = {
  applied: [{ to: 'rejected', label: 'Reject' }],
  evidence: [{ to: 'assessment', label: 'Move to assessment' }, { to: 'rejected', label: 'Reject' }],
  assessment: [{ to: 'decision', label: 'Complete assessment' }, { to: 'rejected', label: 'Reject' }],
  decision: [{ to: 'approved', label: 'Approve + issue certificate' }, { to: 'rejected', label: 'Reject' }],
  approved: [{ to: 'assessment', label: 'Revoke mark' }],
  rejected: [{ to: 'assessment', label: 'Reopen' }],
};
const SCORE_OPTS = [
  { v: 'pass', label: 'Pass', c: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { v: 'partial', label: 'Partial', c: 'bg-amber-100 text-amber-700 border-amber-200' },
  { v: 'fail', label: 'Fail', c: 'bg-red-100 text-red-700 border-red-200' },
];
const fmt = (s) => { try { return new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return s; } };

const AssuranceDetail = ({ appId, onClose, onChanged }) => {
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState({});
  const [recommendation, setRecommendation] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/assurance/applications/${appId}`);
      setA(data);
      setScores((data.assessment || {}).scores || {});
      setRecommendation((data.assessment || {}).recommendation || '');
    } finally { setLoading(false); }
  }, [appId]);
  useEffect(() => { load(); }, [load]);

  const setScore = (key, field, value) => setScores((p) => ({ ...p, [key]: { ...(p[key] || {}), [field]: value } }));

  const saveAssessment = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/assurance/applications/${appId}/assessment`, { scores, recommendation });
      toast.success('Assessment saved');
      load();
    } catch { toast.error('Could not save assessment'); }
    finally { setSaving(false); }
  };

  const move = async (stage) => {
    try {
      const r = await axios.patch(`${API}/assurance/applications/${appId}`, { stage });
      toast.success(stage === 'approved' && r.data.assured ? 'Approved - certificate issued & mark granted' : `Moved to ${stage.replace('_', ' ')}`);
      onChanged(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const resendLink = async () => {
    try {
      const { data } = await axios.post(`${API}/assurance/applications/${appId}/evidence-link`);
      toast.success('Evidence link sent to supplier');
      setA((p) => ({ ...p, evidence_link: data.evidence_link }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send link'); }
  };

  const copyLink = () => { if (a?.evidence_link) { navigator.clipboard.writeText(a.evidence_link); toast.success('Link copied'); } };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="assurance-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{a ? a.company_name : 'Application'}
            {a?.assured && <Badge className="bg-[#1D11AA]/10 text-[#1D11AA] inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Assured</Badge>}
          </DialogTitle>
        </DialogHeader>
        {loading || !a ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : (
          <>
            <div className="space-y-5">
              <div className="text-sm text-gray-600 space-y-1">
                {a.product_name && <p className="font-medium text-gray-800">{a.product_name}</p>}
                <p className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {a.email}{a.contact_name ? ` · ${a.contact_name}` : ''}</p>
                {a.website && <p><a href={a.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#1D11AA] hover:underline">{a.website.replace(/^https?:\/\//, '')} <ExternalLink className="w-3 h-3" /></a></p>}
                {a.notes && <p className="text-gray-500 italic">"{a.notes}"</p>}
                <p className="text-xs text-gray-400">Applied {fmt(a.created_at)} · {a.payment_status === 'paid' ? 'Paid £1,500' : 'Payment pending'}</p>
              </div>

              {/* Evidence link */}
              {a.payment_status === 'paid' && (
                <div className="border border-[#E5E7EB] rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Supplier evidence link</p>
                  <div className="flex items-center gap-2">
                    <input readOnly value={a.evidence_link || ''} className="flex-1 text-xs px-3 py-2 border border-[#E5E7EB] rounded-md bg-gray-50 text-gray-600" data-testid="assurance-evidence-link-input" />
                    <Button size="sm" variant="outline" onClick={copyLink} className="h-8" data-testid="assurance-copy-link"><Copy className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" onClick={resendLink} className="h-8 bg-[#1D11AA] hover:bg-[#160C85]" data-testid="assurance-resend-link"><Send className="w-3.5 h-3.5 mr-1" /> Send</Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">{(a.evidence || {}).submitted_at ? `Submitted ${fmt(a.evidence.submitted_at)}` : 'Not yet submitted by supplier'}</p>
                </div>
              )}

              {/* Assessment against criteria */}
              <div>
                <p className="text-sm font-semibold text-gray-900 mb-3">Structured assessment</p>
                <div className="space-y-4">
                  {a.criteria.map((c, i) => {
                    const ev = (a.evidence || {}).responses?.[c.key] || {};
                    const sc = scores[c.key] || {};
                    return (
                      <div key={c.key} className="border border-[#E5E7EB] rounded-lg p-4" data-testid={`assurance-assess-${c.key}`}>
                        <p className="font-semibold text-gray-800 text-sm">{i + 1}. {c.label}</p>
                        <div className="mt-2 bg-gray-50 rounded-md p-3 text-sm text-gray-700">
                          {ev.answer ? <p className="whitespace-pre-wrap">{ev.answer}</p> : <p className="text-gray-400 italic">No evidence submitted yet.</p>}
                          {ev.link && <a href={ev.link} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-xs text-[#1D11AA] hover:underline">{ev.link} <ExternalLink className="w-3 h-3" /></a>}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {SCORE_OPTS.map((o) => (
                            <button key={o.v} onClick={() => setScore(c.key, 'score', o.v)}
                              className={`text-xs px-3 py-1 rounded-full border transition-colors ${sc.score === o.v ? o.c : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                              data-testid={`assurance-score-${c.key}-${o.v}`}>{o.label}</button>
                          ))}
                        </div>
                        <input placeholder="Assessor note (optional)" value={sc.note || ''} onChange={(e) => setScore(c.key, 'note', e.target.value)}
                          className="w-full mt-2 text-sm px-3 py-2 border border-[#E5E7EB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#1D11AA]/30" data-testid={`assurance-note-${c.key}`} />
                      </div>
                    );
                  })}
                </div>
                <textarea rows={2} placeholder="Overall recommendation" value={recommendation} onChange={(e) => setRecommendation(e.target.value)}
                  className="w-full mt-3 text-sm px-3 py-2 border border-[#E5E7EB] rounded-md focus:outline-none focus:ring-2 focus:ring-[#1D11AA]/30" data-testid="assurance-recommendation" />
                <Button size="sm" onClick={saveAssessment} disabled={saving} className="mt-3 bg-[#1D11AA] hover:bg-[#160C85]" data-testid="assurance-save-assessment">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save assessment
                </Button>
              </div>

              {/* Certificate */}
              {a.certificate && (
                <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4" data-testid="assurance-certificate">
                  <p className="text-sm font-semibold text-emerald-800 inline-flex items-center gap-1.5"><Award className="w-4 h-4" /> Certificate {a.certificate.id}</p>
                  <p className="text-xs text-emerald-700 mt-1">Issued {fmt(a.certificate.issued_at)} · Expires {fmt(a.certificate.expires_at)}</p>
                  {a.due_for_renewal && <p className="text-xs text-amber-700 font-medium mt-1 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {a.expired ? 'Certificate has expired' : `Renewal due in ${a.days_to_expiry} days`}</p>}
                </div>
              )}

              {/* Stage actions */}
              <div className="flex flex-wrap gap-2 pt-1 border-t border-gray-100 pt-4">
                {(NEXT[a.stage] || []).map((n) => (
                  <Button key={n.to} size="sm" variant={n.to === 'rejected' ? 'outline' : 'default'}
                    className={n.to === 'rejected' ? 'text-red-600 border-red-200 hover:bg-red-50' : 'bg-[#1D11AA] hover:bg-[#160C85]'}
                    onClick={() => move(n.to)} data-testid={`assurance-detail-move-${n.to}`}>{n.label}</Button>
                ))}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const AssurancePipeline = () => {
  const [data, setData] = useState({ items: [], counts: {}, paid: 0, revenue: 0, fee: 1500, renewals_due: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/assurance/applications`); setData(r.data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const byStage = (k) => data.items.filter((a) => a.stage === k);

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="assurance-pipeline-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Care Tech Assurance</h1>
        <p className="text-gray-500 text-sm mt-1">Structured independent-assurance programme. Suppliers submit evidence against six criteria; approving issues a 12-month certificate and grants the public assurance mark.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="assurance-stat-paid"><CardContent className="p-5 flex items-center gap-4"><div className="w-11 h-11 rounded-lg bg-[#1D11AA]/10 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-[#1D11AA]" /></div><div><p className="text-2xl font-bold text-gray-900">{data.paid}</p><p className="text-sm text-gray-500">Paid applications</p></div></CardContent></Card>
        <Card data-testid="assurance-stat-revenue"><CardContent className="p-5 flex items-center gap-4"><div className="w-11 h-11 rounded-lg bg-emerald-100 flex items-center justify-center"><PoundSterling className="w-5 h-5 text-emerald-600" /></div><div><p className="text-2xl font-bold text-gray-900">£{Number(data.revenue).toLocaleString()}</p><p className="text-sm text-gray-500">Assurance revenue</p></div></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-2xl font-bold text-emerald-700">{data.counts.approved || 0}</p><p className="text-sm text-gray-500">Assured suppliers</p></CardContent></Card>
        <Card data-testid="assurance-stat-renewals"><CardContent className="p-5 flex items-center gap-4"><div className="w-11 h-11 rounded-lg bg-amber-100 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-amber-600" /></div><div><p className="text-2xl font-bold text-gray-900">{data.renewals_due || 0}</p><p className="text-sm text-gray-500">Renewals due</p></div></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {STAGES.map((st) => (
          <div key={st.key} data-testid={`assurance-column-${st.key}`}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-800">{st.label}</h2>
              <Badge variant="secondary" className={st.color}>{byStage(st.key).length}</Badge>
            </div>
            <div className="space-y-3">
              {byStage(st.key).length === 0 && <p className="text-xs text-gray-400 py-4 text-center border border-dashed border-gray-200 rounded-lg">None</p>}
              {byStage(st.key).map((a) => (
                <Card key={a.id} className="cursor-pointer hover:border-[#1D11AA]/40 transition-colors" onClick={() => setSelected(a.id)} data-testid={`assurance-app-${a.id}`}><CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-gray-900">{a.company_name}</p>
                    {a.payment_status === 'paid' ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Paid</Badge> : <Badge variant="outline" className="text-[10px] text-amber-700">Unpaid</Badge>}
                  </div>
                  {a.product_name && <p className="text-sm text-gray-600 mt-0.5">{a.product_name}</p>}
                  <div className="text-xs text-gray-400 mt-2 space-y-0.5">
                    <p className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {a.email}</p>
                    <p>Applied {fmt(a.created_at)}</p>
                  </div>
                  {a.stage === 'evidence' && ((a.evidence || {}).submitted_at
                    ? <Badge className="bg-blue-100 text-blue-700 text-[10px] mt-2">Evidence submitted</Badge>
                    : <Badge variant="outline" className="text-[10px] text-amber-700 mt-2">Awaiting evidence</Badge>)}
                  {a.assured && <Badge className="bg-[#1D11AA]/10 text-[#1D11AA] text-[10px] mt-2 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Mark live</Badge>}
                  {a.due_for_renewal && <Badge className="bg-amber-100 text-amber-700 text-[10px] mt-2 ml-1 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {a.expired ? 'Expired' : 'Renewal due'}</Badge>}
                </CardContent></Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      {selected && <AssuranceDetail appId={selected} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
};

export default AssurancePipeline;
